import React, { useEffect, useMemo, useState } from "react";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Field from "../../components/ui/Field";
import Input from "../../components/ui/Input";
import PageHeader from "../../components/ui/PageHeader";
import Switch from "../../components/ui/Switch";
import {
    createArtistCategoryRequest,
    getArtistCategoriesRequest,
    updateArtistCategoryRequest,
} from "../../services/api/content.api";
import {
    ArtistCategory,
    ArtistCategoryCreatePayload,
} from "../../services/types/content.types";

interface FormState {
    nameRu: string;
    nameEn: string;
    nameDe: string;
    nameUa: string;
    sort: string;
    isActive: boolean;
}

const initialFormState: FormState = {
    nameRu: "",
    nameEn: "",
    nameDe: "",
    nameUa: "",
    sort: "",
    isActive: true,
};

const ArtistCategoriesPage: React.FC = () => {
    const [categories, setCategories] = useState<ArtistCategory[]>([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const [formState, setFormState] = useState<FormState>(initialFormState);

    const [searchValue, setSearchValue] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [pageError, setPageError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const selectedCategory = useMemo(() => {
        return categories.find((item) => item.id === selectedCategoryId) ?? null;
    }, [categories, selectedCategoryId]);

    const filteredCategories = useMemo(() => {
        const normalizedQuery = searchValue.trim().toLowerCase();

        const sortedItems = [...categories].sort((a, b) => {
            const sortA = a.sort ?? 999999;
            const sortB = b.sort ?? 999999;

            if (sortA !== sortB) {
                return sortA - sortB;
            }

            return a.name_ru.localeCompare(b.name_ru);
        });

        if (!normalizedQuery) {
            return sortedItems;
        }

        return sortedItems.filter((item) => {
            return (
                item.name_ru.toLowerCase().includes(normalizedQuery) ||
                item.name_en.toLowerCase().includes(normalizedQuery) ||
                item.name_de.toLowerCase().includes(normalizedQuery) ||
                (item.name_ua ?? "").toLowerCase().includes(normalizedQuery)
            );
        });
    }, [categories, searchValue]);

    const resetForm = () => {
        setSelectedCategoryId(null);
        setFormState(initialFormState);
        setFormError(null);
        setSuccessMessage(null);
    };

    const syncFormFromCategory = (category: ArtistCategory) => {
        setFormState({
            nameRu: category.name_ru,
            nameEn: category.name_en,
            nameDe: category.name_de,
            nameUa: category.name_ua ?? "",
            sort: category.sort !== null && category.sort !== undefined ? String(category.sort) : "",
            isActive: category.is_active,
        });
    };

    const loadCategories = async () => {
        setIsLoading(true);
        setPageError(null);

        try {
            const response = await getArtistCategoriesRequest();
            setCategories(response);
        } catch (error) {
            const message =
                error instanceof Error ? error.message : "Не удалось загрузить категории";
            setPageError(message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        void loadCategories();
    }, []);

    const handleSelectCategory = (category: ArtistCategory) => {
        setSelectedCategoryId(category.id);
        syncFormFromCategory(category);
        setFormError(null);
        setSuccessMessage(null);
    };

    const handleChange =
        (field: keyof FormState) =>
            (event: React.ChangeEvent<HTMLInputElement>) => {
                const value = event.target.value;

                setFormState((previousState) => ({
                    ...previousState,
                    [field]: value,
                }));
            };

    const validateForm = (): string | null => {
        if (!formState.nameRu.trim()) {
            return "Укажите название на русском языке.";
        }

        if (!formState.nameEn.trim()) {
            return "Укажите название на английском языке.";
        }

        if (!formState.nameDe.trim()) {
            return "Укажите название на немецком языке.";
        }

        if (formState.sort.trim() && Number.isNaN(Number(formState.sort))) {
            return "Поле sort должно содержать число.";
        }

        return null;
    };

    const buildPayload = (): ArtistCategoryCreatePayload => {
        return {
            name_ru: formState.nameRu.trim(),
            name_en: formState.nameEn.trim(),
            name_de: formState.nameDe.trim(),
            name_ua: formState.nameUa.trim() || null,
            sort: formState.sort.trim() ? Number(formState.sort) : null,
            is_active: formState.isActive,
        };
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setFormError(null);
        setSuccessMessage(null);

        const validationError = validateForm();

        if (validationError) {
            setFormError(validationError);
            return;
        }

        setIsSaving(true);

        try {
            const payload = buildPayload();

            if (selectedCategoryId) {
                const updatedCategory = await updateArtistCategoryRequest(selectedCategoryId, payload);

                setCategories((previousState) =>
                    previousState.map((item) =>
                        item.id === updatedCategory.id ? updatedCategory : item
                    )
                );

                setSelectedCategoryId(updatedCategory.id);
                syncFormFromCategory(updatedCategory);
                setSuccessMessage("Категория успешно обновлена.");
            } else {
                const createdCategory = await createArtistCategoryRequest(payload);

                setCategories((previousState) => [createdCategory, ...previousState]);
                setSelectedCategoryId(createdCategory.id);
                syncFormFromCategory(createdCategory);
                setSuccessMessage("Категория успешно создана.");
            }
        } catch (error) {
            const message =
                error instanceof Error ? error.message : "Не удалось сохранить категорию";
            setFormError(message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="page-content">
            <PageHeader
                title="Категории артистов"
                description="Справочник категорий артистов с названиями на русском, английском и немецком языках."
                actions={
                    <Button variant="primary" onClick={resetForm}>
                        Новая категория
                    </Button>
                }
            />

            <section className="content-split">
                <Card className="content-list-card">
                    <div className="section-toolbar">
                        <div>
                            <h3 className="card-title">Список категорий</h3>
                            <p className="card-text">
                                Используйте поиск, чтобы быстро найти нужную запись.
                            </p>
                        </div>

                        <div className="section-toolbar-controls">
                            <Input
                                type="text"
                                placeholder="Поиск по названию..."
                                value={searchValue}
                                onChange={(event) => setSearchValue(event.target.value)}
                            />
                        </div>
                    </div>

                    {pageError ? (
                        <div className="form-global-error">{pageError}</div>
                    ) : isLoading ? (
                        <div className="table-skeleton">
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                        </div>
                    ) : filteredCategories.length === 0 ? (
                        <div className="empty-block">
                            <h3 className="empty-block-title">Категории не найдены</h3>
                            <p className="empty-block-text">
                                Создайте первую категорию или измените поисковый запрос.
                            </p>
                        </div>
                    ) : (
                        <div className="table-wrap">
                            <table className="data-table">
                                <thead>
                                <tr>
                                    <th>RU</th>
                                    <th>EN</th>
                                    <th>DE</th>
                                    <th>UA</th>
                                    <th>Sort</th>
                                    <th>Статус</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredCategories.map((category) => {
                                    const isSelected = category.id === selectedCategoryId;

                                    return (
                                        <tr
                                            key={category.id}
                                            className={isSelected ? "data-table-row-active" : ""}
                                            onClick={() => handleSelectCategory(category)}
                                        >
                                            <td>
                                                <div className="table-primary-cell">
                                                    <span className="table-primary-text">{category.name_ru}</span>
                                                    <span className="table-secondary-text">{category.id}</span>
                                                </div>
                                            </td>
                                            <td>{category.name_en}</td>
                                            <td>{category.name_de}</td>
                                            <td>{category.name_ua || "—"}</td>
                                            <td>{category.sort ?? "—"}</td>
                                            <td>
                          <span
                              className={`status-badge ${
                                  category.is_active
                                      ? "status-badge-active"
                                      : "status-badge-inactive"
                              }`}
                          >
                            {category.is_active ? "Активна" : "Выключена"}
                          </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>

                <Card className="content-form-card">
                    <div className="editor-head">
                        <div>
                            <h3 className="card-title">
                                {selectedCategory ? "Редактирование категории" : "Новая категория"}
                            </h3>
                            <p className="card-text">
                                Заполните названия на трёх языках, порядок сортировки и статус активности.
                            </p>
                        </div>

                        {selectedCategory ? (
                            <span className="editor-badge">ID выбран</span>
                        ) : (
                            <span className="editor-badge editor-badge-soft">Создание</span>
                        )}
                    </div>

                    <form className="editor-form" onSubmit={handleSubmit}>
                        <div className="form-grid">
                            <Field label="Название (RU)" htmlFor="artist-category-name-ru">
                                <Input
                                    id="artist-category-name-ru"
                                    type="text"
                                    placeholder="Например: Поп"
                                    value={formState.nameRu}
                                    onChange={handleChange("nameRu")}
                                />
                            </Field>

                            <Field label="Название (EN)" htmlFor="artist-category-name-en">
                                <Input
                                    id="artist-category-name-en"
                                    type="text"
                                    placeholder="For example: Pop"
                                    value={formState.nameEn}
                                    onChange={handleChange("nameEn")}
                                />
                            </Field>

                            <Field label="Название (DE)" htmlFor="artist-category-name-de">
                                <Input
                                    id="artist-category-name-de"
                                    type="text"
                                    placeholder="Zum Beispiel: Pop"
                                    value={formState.nameDe}
                                    onChange={handleChange("nameDe")}
                                />
                            </Field>

                            <Field label="Название (UA)" htmlFor="artist-category-name-ua">
                                <Input
                                    id="artist-category-name-ua"
                                    type="text"
                                    placeholder="Українською"
                                    value={formState.nameUa}
                                    onChange={handleChange("nameUa")}
                                />
                            </Field>

                            <Field label="Sort" htmlFor="artist-category-sort">
                                <Input
                                    id="artist-category-sort"
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="Например: 10"
                                    value={formState.sort}
                                    onChange={handleChange("sort")}
                                />
                            </Field>
                        </div>

                        <div className="form-switch-row">
                            <Switch
                                checked={formState.isActive}
                                onChange={(checked) =>
                                    setFormState((previousState) => ({
                                        ...previousState,
                                        isActive: checked,
                                    }))
                                }
                                label={formState.isActive ? "Категория активна" : "Категория выключена"}
                            />
                        </div>

                        {formError ? <div className="form-global-error">{formError}</div> : null}
                        {successMessage ? <div className="form-global-success">{successMessage}</div> : null}

                        <div className="editor-actions">
                            <Button type="submit" disabled={isSaving}>
                                {isSaving
                                    ? "Сохраняем..."
                                    : selectedCategory
                                        ? "Сохранить изменения"
                                        : "Создать категорию"}
                            </Button>

                            <Button
                                type="button"
                                variant="secondary"
                                onClick={resetForm}
                                disabled={isSaving}
                            >
                                Очистить форму
                            </Button>
                        </div>
                    </form>
                </Card>
            </section>
        </div>
    );
};

export default ArtistCategoriesPage;
